import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, DollarSign, Clock, FileText, CheckCircle, XCircle, Image, Loader2, ZoomIn, X, CreditCard, ChevronDown, MessageSquare, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateTime } from '../../utils/dateFormatter';

import EventSummaryCard from './EventSummaryCard';
import TrustStrip from './TrustStrip';
import MilestoneCard from './MilestoneCard';
import ReviewTimelineCard from './ReviewTimelineCard';
import ProposalFormModal from './ProposalFormModal';
import ChatDiagnosticPanel from './ChatDiagnosticPanel';
import {
  useModalBackHandler,
  useKeyboardInset,
  useBodyScrollLock,
  useResetViewportZoom,
  useLandscapeDetect,
  useForceScrollToBottom,
} from './useConversationHooks';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Image Lightbox ── */
const ImageLightbox = ({ imageUrl, onClose }) => {
  if (!imageUrl) return null;
  return (
    <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-gray-300 z-[101]"><X size={32} /></button>
      <img src={imageUrl} alt="Full size" className="max-w-full max-h-full object-contain rounded-lg" onClick={e => e.stopPropagation()} />
    </div>
  );
};

/* ── Full-screen image helper ── */
const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

/* ── Proposal Card (inline) ── */
const ProposalCard = ({ proposal, viewerRole, onAccept, onReject, timeRemaining }) => {
  if (!proposal) return null;
  const isPending = proposal.status === 'pending' && !proposal.is_expired;
  const isAccepted = proposal.status === 'accepted';
  const isRejected = proposal.status === 'rejected';
  const isExpired = proposal.status === 'expired' || proposal.is_expired;

  return (
    <div data-testid="proposal-card" className={`mx-3 my-2 rounded-xl border shadow-sm overflow-hidden ${
      isAccepted ? 'border-emerald-200 bg-emerald-50/50' : isRejected ? 'border-red-200 bg-red-50/50' : isExpired ? 'border-slate-200 bg-slate-50' : 'border-blue-200 bg-blue-50/50'
    }`}>
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <FileText className={`w-4 h-4 ${isAccepted ? 'text-emerald-500' : isRejected ? 'text-red-500' : 'text-blue-500'}`} />
          <span className="text-sm font-semibold text-slate-800">
            {isAccepted ? 'Proposal Accepted' : isRejected ? 'Proposal Declined' : isExpired ? 'Proposal Expired' : 'Proposal'}
          </span>
          {isPending && timeRemaining && (
            <span className="ml-auto text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full flex items-center gap-1">
              <Clock className="w-3 h-3" />{timeRemaining.text}
            </span>
          )}
        </div>

        <div className="text-2xl font-bold text-slate-900 mb-1">
          ${proposal.agreed_price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </div>

        {proposal.scope_description && (
          <p className="text-xs text-slate-600 mb-2 line-clamp-3">{proposal.scope_description}</p>
        )}

        {proposal.items && proposal.items.length > 0 && (
          <div className="space-y-1 mb-2">
            {proposal.items.slice(0, 3).map((item, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-slate-600 truncate">{item.description}</span>
                <span className="font-medium text-slate-700 ml-2">${item.amount?.toLocaleString()}</span>
              </div>
            ))}
            {proposal.items.length > 3 && <span className="text-xs text-slate-400">+{proposal.items.length - 3} more items</span>}
          </div>
        )}

        {/* Actions — customer only, pending only */}
        {viewerRole === 'customer' && isPending && !timeRemaining?.expired && (
          <div className="flex gap-2 mt-3 pt-2 border-t border-slate-200/60">
            <Button
              data-testid="accept-proposal-btn"
              onClick={() => onAccept(proposal.id)}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm h-9"
            >
              <CheckCircle className="w-4 h-4 mr-1" /> Accept & Book
            </Button>
            <Button
              data-testid="reject-proposal-btn"
              variant="outline"
              onClick={() => onReject(proposal.id)}
              className="text-sm h-9 border-slate-300"
            >
              <XCircle className="w-4 h-4 mr-1" /> Decline
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Compact Context Bar (sticky, appears on scroll) ── */
const CompactContextBar = ({ eventSummary, inquiry, booking, userRole, onPayNow, onNavigateToEvent, onTap }) => {
  const eventType = inquiry?.event_type || eventSummary?.event_type || 'Event';
  const eventDate = inquiry?.event_date || eventSummary?.event_date;
  const location = inquiry?.location || eventSummary?.location_city || '';
  const shortContext = eventDate
    ? new Date(eventDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : location.split(',')[0]?.trim();

  const depositPaid = booking?.deposit_paid;
  const finalPaid = booking?.final_paid;
  const depositAmount = booking?.deposit_amount || 0;
  const finalAmount = booking?.final_amount || 0;
  const lifecycle = booking?.lifecycle;

  // Centralized cancellation guard: when cancelled, suppress all
  // payment-due labels + CTAs. Keep the event/date/location summary intact
  // so the customer still sees event context. Historical paid amounts are
  // preserved on the booking object for the BookingStatusCard banner below.
  const isCancelled = !!(booking && (
    lifecycle?.is_cancelled === true ||
    ['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'].includes(booking?.status)
  ));

  // Derive display state
  const depositDue = booking && !depositPaid && !isCancelled;
  const finalDue = depositPaid && !finalPaid && !isCancelled;
  const amountDue = depositDue ? depositAmount : finalDue ? finalAmount : 0;
  const isOverdue = !isCancelled && lifecycle?.final_payment?.state === 'overdue';
  const depExpired = !isCancelled && lifecycle?.deposit?.state === 'expired';
  const countdown = isCancelled ? '' : (depositDue
    ? lifecycle?.deposit?.countdown
    : finalDue
      ? lifecycle?.final_payment?.countdown
      : '');

  const paymentLabel = !booking ? null
    : isCancelled ? 'Cancelled'
    : finalPaid ? 'Fully Paid'
    : amountDue > 0 ? `$${amountDue.toLocaleString(undefined, { minimumFractionDigits: 2 })} due`
    : null;

  const showPayBtn = userRole === 'customer' && (depositDue || finalDue) && onPayNow && !depExpired && !isOverdue && !isCancelled;
  const showOverdueMsg = userRole === 'customer' && isOverdue;

  return (
    <div
      data-testid="compact-context-bar"
      className="flex items-center gap-2 px-4 py-1.5 bg-slate-50/95 backdrop-blur-sm border-b border-slate-100 cursor-pointer transition-all duration-200 flex-shrink-0"
      onClick={onTap}
    >
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <button
          type="button"
          onClick={(e) => { if (onNavigateToEvent) { e.stopPropagation(); onNavigateToEvent(); } }}
          className={`text-xs font-semibold truncate max-w-[120px] ${onNavigateToEvent ? 'text-emerald-700 hover:text-emerald-800 hover:underline' : 'text-slate-700'}`}
          data-testid="compact-bar-event-link"
        >
          {eventType}
        </button>
        {shortContext && (
          <>
            <span className="text-slate-300 text-[10px]">·</span>
            <span className="text-[11px] text-slate-500 truncate">{shortContext}</span>
          </>
        )}
      </div>
      {paymentLabel && (
        <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
          {isCancelled ? (
            <span className="flex items-center gap-1 text-[11px] font-medium text-red-600">
              <XCircle className="w-3 h-3" />
              {paymentLabel}
            </span>
          ) : finalPaid ? (
            <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-600">
              <CheckCircle className="w-3 h-3" />
              {paymentLabel}
            </span>
          ) : (
            <span className={`flex items-center gap-1 text-[11px] font-medium ${isOverdue ? 'text-red-600' : 'text-amber-700'}`}>
              <CreditCard className="w-3 h-3" />
              {paymentLabel}
              {countdown && <span className="text-[10px] text-gray-400 ml-0.5">· {countdown}</span>}
            </span>
          )}
          {showPayBtn && booking?.id && (
            <button
              data-testid="compact-bar-complete-payment"
              onClick={() => onPayNow(booking.id)}
              className="text-[10px] font-semibold text-white bg-[#C6A96B] hover:bg-[#B89655] px-2.5 py-0.5 rounded-full transition-colors"
            >
              Complete Payment
            </button>
          )}
          {showOverdueMsg && (
            <span className="text-[10px] font-medium text-red-500 px-2 py-0.5 rounded-full bg-red-50">
              Contact Vendor
            </span>
          )}
        </div>
      )}
      <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 rotate-180" />
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   ConversationThread — Main Component
   ═══════════════════════════════════════════════════ */
const ConversationThread = ({ inquiryId, isOpen, onClose, userRole, onProposalAccepted, inquiryStatus, scrollToPayment, onPayNow, onLeaveReview, __diagSwitcherFlagRaw, __diagSwitcherBranch, __diagSelectedInquiry }) => {
  // ── Conversation data (from hydration endpoint) ──
  const [convData, setConvData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [proposal, setProposal] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  // ── Message composer state ──
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  // ── Proposal action state ──
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [acceptingProposalId, setAcceptingProposalId] = useState(null);
  const [rejectingProposalId, setRejectingProposalId] = useState(null);
  const [customerTermsAccepted, setCustomerTermsAccepted] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showProposalForm, setShowProposalForm] = useState(false);

  // ── Refs ──
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const infoCardsRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const hasInitialScrolled = useRef(false);
  const userScrolledAwayRef = useRef(false);
  const justSentRef = useRef(false);

  // ── Compact header state ──
  const [showCompactBar, setShowCompactBar] = useState(false);

  // ── Diagnostic instrumentation (read-only) ──
  const [diagLastFetchStatus, setDiagLastFetchStatus] = useState('idle');
  const [diagLastFetchError, setDiagLastFetchError] = useState(null);
  const [diagLastAutoScrollReason, setDiagLastAutoScrollReason] = useState('none');
  const [diagUserScrolledAway, setDiagUserScrolledAway] = useState(false);
  const [diagPrevMessageCount, setDiagPrevMessageCount] = useState(0);

  // ── Shared hooks (iOS keyboard, scroll lock, back button) ──
  useModalBackHandler(isOpen, onClose);
  useBodyScrollLock(isOpen);
  const keyboardInset = useKeyboardInset(isOpen);
  const resetViewportZoom = useResetViewportZoom();
  const isLandscape = useLandscapeDetect();
  const { forceScrollToBottom } = useForceScrollToBottom(messagesScrollRef);

  const isCompactMode = isLandscape && keyboardInset > 50;
  // Derive closed status from fetched data (data-driven, not entry-point-dependent)
  const inquiryStatusLive = convData?.inquiry?.status || inquiryStatus;
  const isInquiryClosed = ['declined', 'completed'].includes(inquiryStatusLive);
  const isCancelled = inquiryStatusLive === 'cancelled';
  // Composer lock — typing is disabled on cancelled bookings too. Vendor
  // proposal CTA (canSendProposal) still gates on isInquiryClosed so a
  // rebook proposal is still possible on cancelled.
  const isComposerLocked = isInquiryClosed || isCancelled;

  // ── Close handler ──
  const handleClose = useCallback(() => {
    if (textareaRef.current) textareaRef.current.blur();
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    if (messagesScrollRef.current) messagesScrollRef.current.scrollTop = 0;
    resetViewportZoom();
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    setTimeout(() => onClose(), 100);
  }, [onClose, resetViewportZoom]);

  // ── Fetch conversation data ──
  const fetchConversation = useCallback(async () => {
    if (!inquiryId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/inquiries/${inquiryId}/conversation`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConvData(res.data);
      setMessages(res.data.messages || []);
      setProposal(res.data.proposal || null);
      setBooking(res.data.booking || null);
      setDiagLastFetchStatus('200');
      setDiagLastFetchError(null);
    } catch (err) {
      // Fallback to regular messages endpoint if conversation endpoint is off
      if (err.response?.status === 404) {
        setDiagLastFetchStatus('404→fallback');
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API_URL}/api/inquiries/${inquiryId}/messages`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setMessages(res.data);
          setDiagLastFetchError(null);
        } catch (e2) {
          console.error('Failed to fetch messages:', e2);
          setDiagLastFetchError(e2.message || 'fetch failed');
        }
      } else {
        console.error('Failed to fetch conversation:', err);
        setDiagLastFetchStatus(err.response?.status ? String(err.response.status) : 'error');
        setDiagLastFetchError(err.message || 'fetch failed');
      }
    }
  }, [inquiryId]);

  // ── Smart scroll helpers ──
  const isNearBottom = useCallback(() => {
    const el = messagesScrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  // Track user scroll position
  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el || !isOpen) return;
    const handleScroll = () => {
      const away = !isNearBottom();
      userScrolledAwayRef.current = away;
      setDiagUserScrolledAway(away);
      // Show compact bar when info cards are scrolled past
      const cards = infoCardsRef.current;
      if (cards) {
        setShowCompactBar(el.scrollTop > cards.offsetHeight + 8);
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isOpen, isNearBottom]);

  // ── Polling ──
  useEffect(() => {
    if (isOpen && inquiryId) {
      setLoading(true);
      fetchConversation().finally(() => setLoading(false));
      const interval = setInterval(fetchConversation, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, inquiryId, fetchConversation]);

  // ── Smart scroll on message count change ──
  useEffect(() => {
    if (!isOpen || messages.length === 0) return;
    const prevCount = prevMessageCountRef.current;
    const newCount = messages.length;
    prevMessageCountRef.current = newCount;
    setDiagPrevMessageCount(prevCount);

    // Skip if no actual new messages (just re-render / poll with same data)
    if (newCount <= prevCount) {
      setDiagLastAutoScrollReason(`skipped (newCount=${newCount} <= prev=${prevCount})`);
      return;
    }

    // User just sent a message — always scroll
    if (justSentRef.current) {
      justSentRef.current = false;
      setDiagLastAutoScrollReason(`forced (justSent newCount=${newCount})`);
      forceScrollToBottom();
      return;
    }

    // New incoming message — only scroll if user is near bottom
    if (!userScrolledAwayRef.current) {
      setDiagLastAutoScrollReason(`incoming (nearBottom, newCount=${newCount})`);
      forceScrollToBottom();
    } else {
      setDiagLastAutoScrollReason(`incoming-suppressed (userAway, newCount=${newCount})`);
    }
  }, [messages.length, isOpen, forceScrollToBottom]);

  // ── Initial scroll on open ──
  useEffect(() => {
    if (isOpen) {
      hasInitialScrolled.current = false;
      prevMessageCountRef.current = 0;
      userScrolledAwayRef.current = false;
      setShowCompactBar(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && messages.length > 0 && !hasInitialScrolled.current) {
      hasInitialScrolled.current = true;
      // If opened for payment, stay at top so user sees payment card
      if (scrollToPayment) {
        setDiagLastAutoScrollReason('initial-skipped (scrollToPayment=true)');
        return;
      }
      setDiagLastAutoScrollReason('initial-open');
      const t = setTimeout(forceScrollToBottom, 80);
      return () => clearTimeout(t);
    }
  }, [isOpen, messages.length, forceScrollToBottom, scrollToPayment]);

  // ── Proposal time remaining ──
  const getProposalTimeRemaining = () => {
    if (!proposal?.expires_at) return null;
    const diff = new Date(proposal.expires_at) - new Date();
    if (diff <= 0) return { expired: true, text: 'Expired' };
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return { expired: false, text: h > 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${m}m remaining` };
  };

  // ── Send message ──
  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedImage) return;
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      let imageUrl = null;
      if (selectedImage) {
        setUploadingImage(true);
        try {
          const formData = new FormData();
          formData.append('file', selectedImage);
          const res = await axios.post(`${API_URL}/api/upload/message`, formData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          });
          imageUrl = res.data.url;
        } catch { toast.error('Failed to upload image'); setUploadingImage(false); setSending(false); return; }
        setUploadingImage(false);
      }
      await axios.post(`${API_URL}/api/inquiries/${inquiryId}/messages`,
        { message: newMessage || (imageUrl ? 'Image' : ''), image_url: imageUrl },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );
      setNewMessage('');
      setSelectedImage(null);
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      justSentRef.current = true;
      await fetchConversation();
      forceScrollToBottom();
      setTimeout(forceScrollToBottom, 100);
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  // ── Proposal accept/reject ──
  const handleAcceptProposal = async (proposalId, accept, reason = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const body = { accepted: accept, customer_terms_accepted: accept ? customerTermsAccepted : false };
      if (!accept && reason) body.rejection_reason = reason;
      const res = await axios.post(`${API_URL}/api/proposals/${proposalId}/accept`, body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (accept) {
        toast.success('Proposal accepted! Proceed to pay deposit.');
        setShowAcceptDialog(false); setAcceptingProposalId(null); setCustomerTermsAccepted(false);
        if (onProposalAccepted) onProposalAccepted(res.data.booking);
      } else {
        toast.success('Proposal declined');
        setShowRejectDialog(false); setRejectionReason(''); setRejectingProposalId(null);
      }
      await fetchConversation();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to process proposal';
      if (msg.includes('already')) {
        setShowAcceptDialog(false); setShowRejectDialog(false);
        await fetchConversation();
        toast.info('This proposal has already been processed.');
      } else { toast.error(msg); }
    } finally { setLoading(false); }
  };

  // ── Image handling ──
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleTextareaFocus = useCallback(() => {
    // Only scroll to bottom on focus if user is already near the bottom
    if (!userScrolledAwayRef.current) {
      setTimeout(() => forceScrollToBottom(), 150);
    }
  }, [forceScrollToBottom]);

  const handleTextareaBlur = useCallback(() => {
    setTimeout(resetViewportZoom, 100);
  }, [resetViewportZoom]);

  if (!isOpen) return null;

  const vendorTrust = convData?.vendor_trust;
  const eventSummary = convData?.event_summary;
  const milestones = convData?.milestones || [];
  const inquiry = convData?.inquiry;
  const timeRemaining = getProposalTimeRemaining();

  // ── Navigation handlers (context-aware anchors) ──
  const eventId = inquiry?.event_id || eventSummary?.id;
  const vendorId = inquiry?.vendor_id;

  const handleNavigateToEvent = eventId ? () => {
    window.location.href = `/events/${eventId}`;
  } : null;

  const handleNavigateToVendor = vendorId ? () => {
    window.location.href = `/vendors/${vendorId}`;
  } : null;

  // ── Vendor proposal eligibility ──
  // Uses live proposal state from hydration endpoint (not message snapshots)
  const canSendProposal = (() => {
    if (userRole !== 'vendor') return false;
    if (isInquiryClosed) return false;
    // If there's a booking (proposal was accepted), no more proposals
    if (booking) return false;
    // If no proposal exists, vendor can send one
    if (!proposal) return true;
    // If proposal is pending, don't allow duplicate
    if (proposal.status === 'pending' && !proposal.is_expired) return false;
    // If rejected, expired, or superseded — vendor can send revised
    if (['rejected', 'expired', 'superseded'].includes(proposal.status) || proposal.is_expired) return true;
    // Accepted — no more proposals (booking should exist)
    if (proposal.status === 'accepted') return false;
    return false;
  })();

  const proposalCtaLabel = (() => {
    if (!proposal) return 'Send Proposal';
    if (proposal.status === 'rejected') return 'Send Revised Proposal';
    if (proposal.status === 'expired' || proposal.is_expired) return 'Send New Proposal';
    if (proposal.status === 'superseded') return 'Send New Proposal';
    return 'Send Proposal';
  })();

  // ── Build unified timeline ──
  // Merge messages and milestones by timestamp, deduplicating proposal milestones near proposal messages
  const buildTimeline = () => {
    const items = [];

    // Add messages
    messages.forEach(msg => {
      const kind = msg.is_proposal ? 'proposal_msg'
        : msg.sender_role === 'system' ? 'system_msg'
        : msg.message_type === 'change_request' ? 'change_request'
        : 'message';
      items.push({ kind, ts: msg.created_at, data: msg });
    });

    // Add milestones (skip types that overlap with messages)
    const proposalMsgTimes = new Set(messages.filter(m => m.is_proposal).map(m => m.created_at));
    milestones.forEach(ms => {
      // Skip "Request Sent" — redundant, messages start tells this
      if (ms.title === 'Inquiry Sent' || ms.title === 'Request Sent') return;
      // Skip "Proposal Sent" if there's already a proposal message at same time
      if (ms.title === 'Proposal Sent' && proposalMsgTimes.has(ms.timestamp)) return;
      items.push({ kind: 'milestone', ts: ms.timestamp, data: ms });
    });

    items.sort((a, b) => new Date(a.ts) - new Date(b.ts));
    return items;
  };

  const timeline = buildTimeline();

  // ── Render ──
  return (
    <div data-testid="conversation-overlay" className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[60]" onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <div
        className="absolute inset-0 sm:inset-4 sm:m-auto sm:max-w-2xl md:max-w-3xl sm:max-h-[85vh] sm:rounded-2xl bg-white flex flex-col overflow-hidden shadow-2xl"
        style={{
          height: window.innerWidth < 640
            ? `calc(100dvh - ${keyboardInset}px)`
            : 'auto',
        }}
      >
        {/* ── HEADER: Trust strip ── */}
        {!isCompactMode ? (
          <TrustStrip vendorTrust={vendorTrust} onClose={handleClose} onNavigateToVendor={handleNavigateToVendor} />
        ) : (
          <div className="flex items-center justify-between px-3 py-1 border-b bg-white">
            <button onClick={handleNavigateToVendor || undefined} className={`text-xs text-slate-500 ${handleNavigateToVendor ? 'hover:text-emerald-600 hover:underline cursor-pointer' : ''}`} data-testid="compact-vendor-link">{vendorTrust?.name || 'Chat'}</button>
            <button data-testid="conversation-close-compact" onClick={handleClose} className="p-1 text-slate-400"><X size={18} /></button>
          </div>
        )}

        {/* ── DIAGNOSTIC PANEL (read-only, gated by celey_chat_debug flag) ── */}
        <ChatDiagnosticPanel
          mountedComponent="ConversationThread"
          switcherFlagRaw={__diagSwitcherFlagRaw}
          switcherFlagBranch={__diagSwitcherBranch}
          userRoleProp={userRole}
          inquiryId={inquiryId}
          inquiryStatusProp={inquiryStatus}
          selectedInquiry={__diagSelectedInquiry}
          convData={convData}
          fetchUrl={inquiryId ? `${API_URL}/api/inquiries/${inquiryId}/conversation` : null}
          lastFetchStatus={diagLastFetchStatus}
          lastFetchError={diagLastFetchError}
          loading={loading}
          isOpen={isOpen}
          isCompactMode={isCompactMode}
          isLandscape={isLandscape}
          keyboardInset={keyboardInset}
          showCompactBar={showCompactBar}
          messages={messages}
          prevMessageCount={diagPrevMessageCount}
          userScrolledAway={diagUserScrolledAway}
          lastAutoScrollReason={diagLastAutoScrollReason}
          scrollMetrics={() => {
            const el = messagesScrollRef.current;
            if (!el) return {};
            return { scrollTop: el.scrollTop, scrollHeight: el.scrollHeight, clientHeight: el.clientHeight };
          }}
        />


        {/* ── COMPACT CONTEXT BAR (always appears when event details scroll out of view) ── */}
        {showCompactBar && (
          <CompactContextBar
            eventSummary={eventSummary}
            inquiry={inquiry}
            booking={booking}
            userRole={userRole}
            onPayNow={onPayNow}
            onNavigateToEvent={handleNavigateToEvent}
            onTap={() => {
              const el = messagesScrollRef.current;
              if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        )}

        {/* ── SCROLLABLE CONTENT ── */}
        <div ref={messagesScrollRef} className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Unified event + payment card (tracked for compact bar trigger) */}
          <div ref={infoCardsRef}>
            {!isCompactMode && (eventSummary || inquiry || booking) && (
              <EventSummaryCard eventSummary={eventSummary} inquiry={inquiry} booking={booking} viewerRole={userRole} onPayNow={onPayNow} onNavigateToEvent={handleNavigateToEvent} />
            )}
          </div>

          {/* Timeline items */}
          <div className="py-2">
            {loading && messages.length === 0 && (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
            )}

            {!loading && messages.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="chat-empty-state">
                <MessageSquare className="w-8 h-8 text-slate-200 mb-3" />
                <p className="text-sm text-slate-400">No messages yet</p>
                <p className="text-xs text-slate-300 mt-1">Send a message to start the conversation</p>
              </div>
            )}

            {/* Date separator helper */}
            {(() => {
              let lastDate = null;
              return timeline.map((item, idx) => {
                const itemDate = new Date(item.ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                const showDate = itemDate !== lastDate;
                lastDate = itemDate;

                return (
                  <React.Fragment key={idx}>
                    {showDate && (
                      <div className="flex items-center gap-3 px-6 py-2">
                        <div className="flex-1 h-px bg-slate-100" />
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{itemDate}</span>
                        <div className="flex-1 h-px bg-slate-100" />
                      </div>
                    )}

                    {item.kind === 'message' && <MessageBubble msg={item.data} userRole={userRole} onImageClick={setLightboxImage} />}
                    {item.kind === 'change_request' && <ChangeRequestCard msg={item.data} userRole={userRole} />}
                    {item.kind === 'system_msg' && <SystemMessage msg={item.data} />}
                    {item.kind === 'proposal_msg' && null /* Rendered via live proposal card below */}
                    {item.kind === 'milestone' && (
                      // Pending payment milestones are suppressed — PaymentStatusCard is the single payment surface.
                      // Only completed/historical payment milestones render (as standard milestone cards).
                      ['Final Payment Due', 'Final Payment Overdue', 'Deposit Due', 'Deposit Expired'].includes(item.data.title)
                        ? null
                        : item.data.title === 'Event Completed' || item.data.title === 'Review Submitted'
                          ? <ReviewTimelineCard milestone={item.data} onLeaveReview={userRole === 'customer' ? onLeaveReview : undefined} />
                          : <MilestoneCard milestone={item.data} />
                    )}
                  </React.Fragment>
                );
              });
            })()}

            {/* Live proposal card — always rendered from live state, not message snapshot */}
            {proposal && proposal.status !== 'superseded' && (
              <ProposalCard
                proposal={proposal}
                viewerRole={userRole}
                timeRemaining={timeRemaining}
                onAccept={(id) => { setAcceptingProposalId(id); setCustomerTermsAccepted(false); setShowAcceptDialog(true); }}
                onReject={(id) => { setRejectingProposalId(id); setRejectionReason(''); setShowRejectDialog(true); }}
              />
            )}
          </div>

          {/* Bottom spacer */}
          <div className="h-2" />
        </div>

        {/* ── VENDOR PROPOSAL CTA ── */}
        {canSendProposal && (
          <div className="border-t bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2.5 flex-shrink-0">
            <button
              data-testid="send-proposal-cta"
              onClick={() => setShowProposalForm(true)}
              className="w-full flex items-center justify-center gap-2 bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300 rounded-xl py-2 px-4 text-sm font-semibold transition-all shadow-sm"
            >
              <FileText size={16} />
              {proposalCtaLabel}
            </button>
          </div>
        )}

        {/* ── COMPOSER ── */}
        {!isComposerLocked && (
          <div className={`border-t bg-white flex-shrink-0 ${isCompactMode ? 'px-2 py-1' : 'px-3 py-2'}`}>
            {/* Image preview */}
            {imagePreview && (
              <div className="mb-2 relative inline-block">
                <img src={imagePreview} alt="Preview" className="h-16 w-16 object-cover rounded-lg border" />
                <button onClick={() => { setSelectedImage(null); setImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs shadow">
                  <X size={12} />
                </button>
              </div>
            )}

            <div className="flex items-end gap-2">
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
              <button
                data-testid="attach-image-btn"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors shrink-0"
              >
                <Image size={isCompactMode ? 18 : 20} />
              </button>

              <textarea
                ref={textareaRef}
                data-testid="message-input"
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onFocus={handleTextareaFocus}
                onBlur={handleTextareaBlur}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                placeholder="Type a message..."
                rows={1}
                className={`flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-emerald-300 focus:ring-1 focus:ring-emerald-200 transition-all outline-none ${
                  isCompactMode ? 'px-3 py-1.5 text-sm max-h-16' : 'px-4 py-2.5 text-sm max-h-24'
                }`}
                style={{ minHeight: isCompactMode ? '32px' : '40px' }}
              />

              <button
                data-testid="send-message-btn"
                onClick={handleSendMessage}
                disabled={sending || (!newMessage.trim() && !selectedImage)}
                className={`shrink-0 rounded-full flex items-center justify-center transition-all ${
                  sending || (!newMessage.trim() && !selectedImage)
                    ? 'bg-slate-100 text-slate-300'
                    : 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm'
                } ${isCompactMode ? 'w-8 h-8' : 'w-10 h-10'}`}
              >
                {sending || uploadingImage ? <Loader2 size={isCompactMode ? 16 : 18} className="animate-spin" /> : <Send size={isCompactMode ? 16 : 18} />}
              </button>
            </div>
          </div>
        )}

        {/* Closed banner */}
        {isComposerLocked && (
          <div className="px-4 py-3 bg-slate-50 border-t text-center">
            <span className="text-sm text-slate-500">
              {isCancelled ? 'Messaging is closed for this booking.' : 'This conversation is closed'}
            </span>
          </div>
        )}
      </div>

      {/* ── Accept Proposal Dialog ── */}
      {showAcceptDialog && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={() => setShowAcceptDialog(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Accept Proposal</h3>
            <p className="text-sm text-slate-600 mb-4">
              Accepting this proposal will create a booking. You'll need to pay a deposit to confirm your reservation.
            </p>
            {proposal && (
              <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
                <span className="text-slate-500">Total:</span>
                <span className="font-bold text-slate-900 ml-2">${proposal.agreed_price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            <label className="flex items-start gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                data-testid="accept-terms-checkbox"
                checked={customerTermsAccepted}
                onChange={e => setCustomerTermsAccepted(e.target.checked)}
                className="mt-0.5 rounded border-slate-300"
              />
              <span className="text-xs text-slate-600">
                I accept the terms and understand that a deposit will be required to confirm this booking.
              </span>
            </label>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAcceptDialog(false)} className="flex-1">Cancel</Button>
              <Button
                data-testid="confirm-accept-btn"
                disabled={!customerTermsAccepted || loading}
                onClick={() => handleAcceptProposal(acceptingProposalId, true)}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Proposal Dialog ── */}
      {showRejectDialog && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={() => setShowRejectDialog(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Decline Proposal</h3>
            <p className="text-sm text-slate-600 mb-3">Let the provider know why you're declining so they can improve their offer.</p>
            <textarea
              data-testid="rejection-reason-input"
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              placeholder="Optional: share a reason..."
              rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mb-4 focus:ring-1 focus:ring-slate-300 outline-none"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowRejectDialog(false)} className="flex-1">Cancel</Button>
              <Button
                data-testid="confirm-reject-btn"
                onClick={() => handleAcceptProposal(rejectingProposalId, false, rejectionReason || 'No reason provided')}
                disabled={loading}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                Decline
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      <ImageLightbox imageUrl={lightboxImage} onClose={() => setLightboxImage(null)} />

      {/* ── Inline Proposal Form (vendor-only) ── */}
      {showProposalForm && (
        <ProposalFormModal
          inquiryId={inquiryId}
          existingProposal={proposal}
          onClose={() => setShowProposalForm(false)}
          onProposalSent={() => {
            setShowProposalForm(false);
            toast.success('Proposal sent!');
            fetchConversation();
            setTimeout(forceScrollToBottom, 500);
          }}
        />
      )}
    </div>
  );
};

/* ── Message Bubble ── */
/* ── Change Request Card ── */
const ChangeRequestCard = ({ msg, userRole }) => {
  const isOwn = msg.sender_role === userRole;
  // Strip the "[Change Request]\n" prefix from the message
  const body = (msg.message || '').replace(/^\[Change Request\]\n?/, '');
  return (
    <div data-testid="change-request-card" className="px-4 py-1.5">
      <div className={`max-w-[88%] sm:max-w-[75%] ${isOwn ? 'ml-auto' : ''}`}>
        <div className="rounded-xl border border-amber-200 bg-amber-50/80 overflow-hidden">
          <div className="flex items-center gap-2 px-3.5 py-2 bg-amber-100/60 border-b border-amber-200/60">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
            <span className="text-xs font-semibold text-amber-700">Change Request</span>
          </div>
          <div className="px-3.5 py-2.5">
            <p className="text-sm text-amber-900 leading-relaxed whitespace-pre-wrap break-words">{body}</p>
          </div>
        </div>
        <span className={`text-[10px] mt-0.5 block ${isOwn ? 'text-right text-slate-400' : 'text-slate-400 ml-1'}`}>
          {formatDateTime ? formatDateTime(msg.created_at) : new Date(msg.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
};

const MessageBubble = ({ msg, userRole, onImageClick }) => {
  const isOwn = msg.sender_role === userRole;
  const imgUrl = msg.image_url ? getImageUrl(msg.image_url) : null;

  return (
    <div data-testid="message-bubble" className={`flex px-4 py-0.5 ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] sm:max-w-[70%] ${isOwn ? 'order-1' : 'order-1'}`}>
        {/* Sender name */}
        {!isOwn && (
          <span className="text-[10px] text-slate-400 font-medium ml-1 mb-0.5 block">{msg.sender_name}</span>
        )}
        <div className={`rounded-2xl px-3.5 py-2 ${
          isOwn
            ? 'bg-emerald-500 text-white rounded-br-md'
            : 'bg-slate-100 text-slate-800 rounded-bl-md'
        }`}>
          {imgUrl && (
            <div className="mb-1.5 cursor-pointer" onClick={() => onImageClick(imgUrl)}>
              <img src={imgUrl} alt="" className="max-w-48 max-h-48 rounded-lg object-cover" loading="lazy" />
              <ZoomIn className={`w-3 h-3 mt-0.5 ${isOwn ? 'text-emerald-200' : 'text-slate-400'}`} />
            </div>
          )}
          {msg.message && msg.message !== 'Image' && (
            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{msg.message}</p>
          )}
        </div>
        <span className={`text-[10px] mt-0.5 block ${isOwn ? 'text-right text-slate-400' : 'text-slate-400 ml-1'}`}>
          {formatDateTime ? formatDateTime(msg.created_at) : new Date(msg.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
};

/* ── System Message ── */
const SystemMessage = ({ msg }) => (
  <div data-testid="system-message" className="flex justify-center px-6 py-1">
    <span className="text-[11px] text-slate-400 bg-slate-50 px-3 py-1 rounded-full text-center max-w-[90%] truncate">
      {msg.message}
    </span>
  </div>
);

export default ConversationThread;
